/**
 * Polyline Drawing Manager
 * google.maps.drawing.DrawingManager の代替実装（Polyline用）
 */

// google.maps.event互換のイベント管理を提供するシンボル
const EVENT_HANDLERS_KEY = Symbol("eventHandlers");

// カスタムオーバーレイでクリックを検知（動的に生成）
function createDrawingOverlay(
    map: google.maps.Map,
    clickHandler: (latLng: google.maps.LatLng) => void,
    dblClickHandler: (latLng: google.maps.LatLng) => void,
    mouseMoveHandler: (latLng: google.maps.LatLng) => void
): google.maps.OverlayView {
    class DrawingOverlay extends google.maps.OverlayView {
        private div: HTMLDivElement | null = null;
        private clickHandlerFn: ((e: MouseEvent) => void) | null = null;
        private dblClickHandlerFn: ((e: MouseEvent) => void) | null = null;
        private mouseMoveHandlerFn: ((e: MouseEvent) => void) | null = null;

        constructor() {
            super();
            this.setMap(map);

            this.clickHandlerFn = (e: MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
                const projection = this.getProjection();
                if (projection) {
                    const bounds = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                    const point = new google.maps.Point(
                        e.clientX - bounds.left,
                        e.clientY - bounds.top
                    );
                    const latLng = projection.fromContainerPixelToLatLng(point);
                    if (latLng) {
                        console.log("[DrawingOverlay] Click detected", latLng.toString());
                        clickHandler(latLng);
                    }
                }
            };

            this.dblClickHandlerFn = (e: MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
                const projection = this.getProjection();
                if (projection) {
                    const bounds = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                    const point = new google.maps.Point(
                        e.clientX - bounds.left,
                        e.clientY - bounds.top
                    );
                    const latLng = projection.fromContainerPixelToLatLng(point);
                    if (latLng) {
                        console.log("[DrawingOverlay] Double-click detected", latLng.toString());
                        dblClickHandler(latLng);
                    }
                }
            };

            this.mouseMoveHandlerFn = (e: MouseEvent) => {
                const projection = this.getProjection();
                if (projection) {
                    const bounds = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                    const point = new google.maps.Point(
                        e.clientX - bounds.left,
                        e.clientY - bounds.top
                    );
                    const latLng = projection.fromContainerPixelToLatLng(point);
                    if (latLng) {
                        mouseMoveHandler(latLng);
                    }
                }
            };
        }

        onAdd() {
            this.div = document.createElement("div");
            this.div.style.position = "absolute";
            this.div.style.cursor = "crosshair";
            this.div.style.backgroundColor = "rgba(255, 0, 0, 0.01)"; // ほぼ透明だが存在を確認可能
            this.div.style.pointerEvents = "auto"; // クリックを受け取る

            // デバッグ用：クリックイベントが発火しているか確認
            const testHandler = (e: MouseEvent) => {
                console.log("[DrawingOverlay] RAW click event fired!", e);
            };
            this.div.addEventListener("click", testHandler);
            this.div.addEventListener("mousedown", (e) => {
                console.log("[DrawingOverlay] mousedown event fired!", e);
            });
            this.div.addEventListener("mouseup", (e) => {
                console.log("[DrawingOverlay] mouseup event fired!", e);
            });

            if (this.clickHandlerFn) {
                this.div.addEventListener("click", this.clickHandlerFn);
            }
            if (this.dblClickHandlerFn) {
                this.div.addEventListener("dblclick", this.dblClickHandlerFn);
            }
            if (this.mouseMoveHandlerFn) {
                this.div.addEventListener("mousemove", this.mouseMoveHandlerFn);
            }

            const panes = this.getPanes();
            console.log("[DrawingOverlay] Available panes:", panes);
            if (panes) {
                // floatPane を使用（マーカーより上位のレイヤー）
                panes.floatPane.appendChild(this.div);
            }
            console.log("[DrawingOverlay] Overlay added to map, div:", this.div);
        }

        draw() {
            // div のサイズを明示的に設定
            if (this.div) {
                const projection = this.getProjection();
                const map = this.getMap() as google.maps.Map;
                if (projection && map) {
                    const bounds = map.getBounds();
                    if (bounds) {
                        const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
                        const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());

                        if (ne && sw) {
                            // マップ全体をカバーするように設定
                            this.div.style.left = sw.x + "px";
                            this.div.style.top = ne.y + "px";
                            this.div.style.width = (ne.x - sw.x) + "px";
                            this.div.style.height = (sw.y - ne.y) + "px";
                            console.log("[DrawingOverlay] draw() - div size set:", {
                                left: sw.x,
                                top: ne.y,
                                width: ne.x - sw.x,
                                height: sw.y - ne.y
                            });
                        }
                    }
                }
            }
        }

        onRemove() {
            if (this.div) {
                if (this.clickHandlerFn) {
                    this.div.removeEventListener("click", this.clickHandlerFn);
                }
                if (this.dblClickHandlerFn) {
                    this.div.removeEventListener("dblclick", this.dblClickHandlerFn);
                }
                if (this.mouseMoveHandlerFn) {
                    this.div.removeEventListener("mousemove", this.mouseMoveHandlerFn);
                }
                this.div.parentNode?.removeChild(this.div);
                this.div = null;
            }
            console.log("[DrawingOverlay] Overlay removed from map");
        }
    }

    return new DrawingOverlay();
}

export class LineDrawingManager {
    private map: google.maps.Map | null = null;
    private polyline: google.maps.Polyline | null = null;
    private polylineOptions: google.maps.PolylineOptions;
    private path: google.maps.LatLng[] = [];
    private listeners: { [key: string]: google.maps.MapsEventListener[] } = {};
    private drawingOverlay: google.maps.OverlayView | null = null;
    private isDrawing: boolean = false;
    private cursorPolyline: google.maps.Polyline | null = null; // カーソルまでのプレビュー線
    [EVENT_HANDLERS_KEY]: { [key: string]: ((arg: any) => void)[] } = {};

    constructor(options: {
        map?: google.maps.Map;
        polylineOptions?: google.maps.PolylineOptions;
        drawingMode?: string | null;
        drawingControl?: boolean;
    }) {
        console.log("[LineDrawingManager] Constructor called", {
            hasMap: !!options.map,
            drawingMode: options.drawingMode,
            polylineOptions: options.polylineOptions
        });
        this.map = options.map || null;
        this.polylineOptions = options.polylineOptions || {
            strokeColor: "#FF0000",
            strokeOpacity: 1.0,
            strokeWeight: 2,
            editable: true
        };

        // drawingModeが指定されている場合は描画を開始
        if (this.map && options.drawingMode) {
            this.startDrawing();
        }
    }

    /**
     * 描画モードを設定
     */
    public setDrawingMode(mode: string | null) {
        if (mode === "polyline") {
            this.startDrawing();
        } else {
            this.stopDrawing();
        }
    }

    /**
     * マップを設定
     */
    public setMap(map: google.maps.Map | null) {
        if (this.isDrawing) {
            this.stopDrawing();
        }
        this.map = map;
    }

    /**
     * 描画を開始
     */
    private startDrawing() {
        console.log("[LineDrawingManager] startDrawing called", { hasMap: !!this.map, isDrawing: this.isDrawing });
        if (!this.map || this.isDrawing) return;

        this.isDrawing = true;
        this.path = [];

        // マップのドラッグとズームを無効化
        this.map.setOptions({
            draggable: false,
            disableDoubleClickZoom: true
        });
        console.log("[LineDrawingManager] Drawing started, draggable disabled");

        // カスタムオーバーレイを使用してクリックを検知
        this.drawingOverlay = createDrawingOverlay(
            this.map,
            (latLng: google.maps.LatLng) => {
                console.log("[LineDrawingManager] Click detected via overlay", latLng.toString());
                this.addPoint(latLng);
            },
            (latLng: google.maps.LatLng) => {
                console.log("[LineDrawingManager] Double-click detected via overlay");
                this.completeDrawing();
            },
            (latLng: google.maps.LatLng) => {
                this.updateCursorPolyline(latLng);
            }
        );
    }

    /**
     * 描画を停止
     */
    private stopDrawing() {
        if (!this.isDrawing) return;

        this.isDrawing = false;

        // マップのオプションを元に戻す
        if (this.map) {
            this.map.setOptions({
                draggable: true,
                disableDoubleClickZoom: false
            });
        }

        // オーバーレイを削除
        if (this.drawingOverlay) {
            this.drawingOverlay.setMap(null);
            this.drawingOverlay = null;
        }

        // プレビュー線を削除
        if (this.cursorPolyline) {
            this.cursorPolyline.setMap(null);
            this.cursorPolyline = null;
        }

        // 描画中のポリラインを削除
        if (this.polyline && this.polyline.getPath().getLength() === 0) {
            this.polyline.setMap(null);
            this.polyline = null;
        }

        this.path = [];
    }

    /**
     * カーソル位置までのプレビュー線を更新
     */
    private updateCursorPolyline(latLng: google.maps.LatLng) {
        if (this.path.length === 0) return;

        const previewPath = [...this.path, latLng];

        if (!this.cursorPolyline) {
            // プレビュー線を作成
            this.cursorPolyline = new google.maps.Polyline({
                strokeColor: this.polylineOptions.strokeColor,
                strokeOpacity: this.polylineOptions.strokeOpacity || 0.6,
                strokeWeight: this.polylineOptions.strokeWeight,
                map: this.map,
                path: previewPath,
                clickable: false
            });
        } else {
            // 既存のプレビュー線のパスを更新
            this.cursorPolyline.setPath(previewPath);
        }
    }

    /**
     * 点を追加
     */
    private addPoint(latLng: google.maps.LatLng) {
        console.log("[LineDrawingManager] addPoint", latLng.toString(), "path length:", this.path.length);
        this.path.push(latLng);

        if (!this.polyline) {
            // 新しいポリラインを作成
            console.log("[LineDrawingManager] Creating new polyline");
            this.polyline = new google.maps.Polyline({
                ...this.polylineOptions,
                map: this.map,
                path: this.path
            });
        } else {
            // 既存のポリラインに点を追加
            console.log("[LineDrawingManager] Adding point to existing polyline");
            this.polyline.getPath().push(latLng);
        }

        // プレビュー線をリセット（次のポイントまでのプレビューを表示するため）
        if (this.cursorPolyline) {
            this.cursorPolyline.setMap(null);
            this.cursorPolyline = null;
        }
    }

    /**
     * 描画完了
     */
    private completeDrawing() {
        console.log("[LineDrawingManager] completeDrawing called", {
            hasPolyline: !!this.polyline,
            pathLength: this.path.length
        });

        if (!this.polyline || this.path.length < 2) {
            // 最低2点必要
            console.log("[LineDrawingManager] completeDrawing - insufficient points, aborting");
            if (this.polyline) {
                this.polyline.setMap(null);
                this.polyline = null;
            }
            this.stopDrawing();
            return;
        }

        const completedPolyline = this.polyline;
        console.log("[LineDrawingManager] completeDrawing - triggering events");

        // 描画モードを停止
        this.stopDrawing();

        // polylinecompleteイベントを発火
        this.trigger("polylinecomplete", completedPolyline);

        // overlaycompleteイベントを発火
        this.trigger("overlaycomplete", {
            type: "polyline",
            overlay: completedPolyline
        });

        this.polyline = null;
    }

    /**
     * 描画を強制完成（仮確定用）
     */
    public forceComplete() {
        console.log("[LineDrawingManager] forceComplete called", {
            hasPolyline: !!this.polyline,
            pathLength: this.path.length,
            isDrawing: this.isDrawing
        });
        this.completeDrawing();
    }

    /**
     * イベントリスナーを追加
     */
    public addListener(eventName: string, handler: (arg: any) => void): google.maps.MapsEventListener {
        if (!this[EVENT_HANDLERS_KEY][eventName]) {
            this[EVENT_HANDLERS_KEY][eventName] = [];
        }
        this[EVENT_HANDLERS_KEY][eventName].push(handler);

        // google.maps.MapsEventListener互換のオブジェクトを返す
        return {
            remove: () => {
                const handlers = this[EVENT_HANDLERS_KEY][eventName];
                if (handlers) {
                    const index = handlers.indexOf(handler);
                    if (index > -1) {
                        handlers.splice(index, 1);
                    }
                }
            }
        } as google.maps.MapsEventListener;
    }

    /**
     * イベントを発火
     */
    private trigger(eventName: string, arg: any) {
        const handlers = this[EVENT_HANDLERS_KEY][eventName];
        console.log("[LineDrawingManager] trigger event:", eventName, "handlers:", handlers?.length || 0);
        if (handlers) {
            handlers.forEach(handler => handler(arg));
        }
    }

    /**
     * 特定のイベントのリスナーをすべてクリア
     */
    public clearListeners(eventName: string) {
        if (this[EVENT_HANDLERS_KEY][eventName]) {
            this[EVENT_HANDLERS_KEY][eventName] = [];
        }
    }

    /**
     * すべてのイベントリスナーをクリア
     */
    public clearAllListeners() {
        this[EVENT_HANDLERS_KEY] = {};
    }

    /**
     * クリーンアップ
     */
    public destroy() {
        this.stopDrawing();
        this.clearAllListeners();
        if (this.polyline) {
            this.polyline.setMap(null);
            this.polyline = null;
        }
        if (this.cursorPolyline) {
            this.cursorPolyline.setMap(null);
            this.cursorPolyline = null;
        }
        if (this.drawingOverlay) {
            this.drawingOverlay.setMap(null);
            this.drawingOverlay = null;
        }
        this.map = null;
    }
}

// google.maps.event互換のヘルパー関数
export const addLineDrawingListener = (
    instance: LineDrawingManager,
    eventName: string,
    handler: (arg: any) => void
): google.maps.MapsEventListener => {
    return instance.addListener(eventName, handler);
};

export const clearLineDrawingListeners = (
    instance: LineDrawingManager,
    eventName: string
) => {
    instance.clearListeners(eventName);
};
